import React, { useState, useEffect, useCallback } from 'react';
import { 
  UserCheck, Plus, Edit3, Trash2, Search, Save, X, 
  AlertTriangle, Shield, Phone, Mail, Calendar, Eye
} from 'lucide-react';
import { createNominee, NOMINEE_TYPES, NOMINEE_STATUS, validateNominee } from '../../data/models/Nominee';

const STORAGE_KEY = 'medfast_nominees';

const NomineeManagement = () => {
  const [nominees, setNominees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', relationship: '', phone: '', email: '',
    nomineeType: '', status: 'active', participantId: '',
    courtOrder: '', courtOrderDate: '', courtOrderNumber: '',
    authorityScope: {
      financialLimit: 'unlimited', serviceBookingAccess: true, planDetailsAccess: true,
      budgetAccess: true, claimsAccess: true, clinicalNotesAccess: false, restrictions: []
    }
  });

  const load = useCallback(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setNominees(data);
    } catch { setNominees([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setNominees(data);
  };

  const filtered = nominees.filter(n => {
    const matchSearch = `${n.firstName} ${n.lastName} ${n.participantId}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || n.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData({
      firstName: '', lastName: '', relationship: '', phone: '', email: '',
      nomineeType: '', status: 'active', participantId: '',
      courtOrder: '', courtOrderDate: '', courtOrderNumber: '',
      authorityScope: {
        financialLimit: 'unlimited', serviceBookingAccess: true, planDetailsAccess: true,
        budgetAccess: true, claimsAccess: true, clinicalNotesAccess: false, restrictions: []
      }
    });
    setShowForm(true);
    setError(null);
  };

  const openEdit = (nominee) => {
    setEditingId(nominee.id);
    setFormData({ ...nominee });
    setShowForm(true);
    setError(null);
  };

  const handleSave = () => {
    const validation = validateNominee(formData);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }

    const now = new Date().toISOString();
    if (editingId) {
      const updated = nominees.map(n => n.id === editingId ? { ...n, ...formData, updatedAt: now } : n);
      save(updated);
    } else {
      const newItem = createNominee({ ...formData, id: `NOM-${Date.now()}` });
      save([...nominees, newItem]);
    }
    setShowForm(false);
    setError(null);
  };

  const handleDelete = (id) => {
    save(nominees.filter(n => n.id !== id));
    setDeleteConfirm(null);
  };

  const viewing = viewingId ? nominees.find(n => n.id === viewingId) : null;

  const statusColor = (s) => {
    if (s === 'active') return 'bg-green-100 text-green-800';
    if (s === 'suspended') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">NDIS Nominees</h2>
          <p className="text-gray-600 text-sm">Manage plan nominees, correspondence nominees, and court-appointed guardians</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium">
          <Plus className="w-4 h-4" /> Add Nominee
        </button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search nominees..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
          <option value="all">All Statuses</option>
          {Object.values(NOMINEE_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No nominees found. Add one to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map(n => (
            <div key={n.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-teal-100 rounded-xl">
                    <UserCheck className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{n.firstName} {n.lastName}</h3>
                    <p className="text-sm text-gray-600">{n.nomineeType || 'Unknown type'} — {n.relationship || 'N/A'}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {n.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{n.phone}</span>}
                      {n.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{n.email}</span>}
                      {n.participantId && <span>Participant: {n.participantId}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColor(n.status)}`}>{n.status}</span>
                  <button onClick={() => setViewingId(n.id)} className="p-1.5 hover:bg-gray-100 rounded-lg" title="View"><Eye className="w-4 h-4 text-gray-600" /></button>
                  <button onClick={() => openEdit(n)} className="p-1.5 hover:bg-teal-100 rounded-lg" title="Edit"><Edit3 className="w-4 h-4 text-teal-600" /></button>
                  <button onClick={() => setDeleteConfirm(n.id)} className="p-1.5 hover:bg-red-100 rounded-lg" title="Delete"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail View */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b bg-teal-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-teal-800">{viewing.firstName} {viewing.lastName}</h3>
              <button onClick={() => setViewingId(null)} className="p-1 hover:bg-teal-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-medium text-gray-500">Type</span><p>{viewing.nomineeType}</p></div>
                <div><span className="font-medium text-gray-500">Status</span><p><span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(viewing.status)}`}>{viewing.status}</span></p></div>
                <div><span className="font-medium text-gray-500">Relationship</span><p>{viewing.relationship || '—'}</p></div>
                <div><span className="font-medium text-gray-500">Participant ID</span><p>{viewing.participantId || '—'}</p></div>
                <div><span className="font-medium text-gray-500">Phone</span><p>{viewing.phone || '—'}</p></div>
                <div><span className="font-medium text-gray-500">Email</span><p>{viewing.email || '—'}</p></div>
              </div>
              {viewing.nomineeType === NOMINEE_TYPES.COURT_APPOINTED_GUARDIAN && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Court Order Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div><span className="font-medium text-gray-500">Court Order</span><p>{viewing.courtOrder || '—'}</p></div>
                    <div><span className="font-medium text-gray-500">Order Number</span><p>{viewing.courtOrderNumber || '—'}</p></div>
                    <div><span className="font-medium text-gray-500">Order Date</span><p>{viewing.courtOrderDate || '—'}</p></div>
                  </div>
                </div>
              )}
              {viewing.authorityScope && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Authority Scope</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(viewing.authorityScope).filter(([k]) => k !== 'restrictions' && k !== 'financialLimit').map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2">
                        {v ? <Shield className="w-3.5 h-3.5 text-green-600" /> : <X className="w-3.5 h-3.5 text-gray-400" />}
                        <span className="text-gray-700 capitalize">{k.replace(/([A-Z])/g, ' $1')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b bg-teal-600 text-white flex justify-between items-center">
              <h2 className="text-xl font-bold">{editingId ? 'Edit Nominee' : 'Add Nominee'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input type="text" value={formData.firstName} onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input type="text" value={formData.lastName} onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nominee Type *</label>
                  <select value={formData.nomineeType} onChange={e => setFormData(p => ({ ...p, nomineeType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
                    <option value="">Select type...</option>
                    {Object.values(NOMINEE_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                  <input type="text" value={formData.relationship} onChange={e => setFormData(p => ({ ...p, relationship: e.target.value }))}
                    placeholder="e.g. Parent, Guardian" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={formData.phone || ''} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.email || ''} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Participant ID</label>
                  <input type="text" value={formData.participantId || ''} onChange={e => setFormData(p => ({ ...p, participantId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500">
                    {Object.values(NOMINEE_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {formData.nomineeType === NOMINEE_TYPES.COURT_APPOINTED_GUARDIAN && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-teal-600" />Court Order Details</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Court Order *</label>
                      <input type="text" value={formData.courtOrder || ''} onChange={e => setFormData(p => ({ ...p, courtOrder: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
                      <input type="text" value={formData.courtOrderNumber || ''} onChange={e => setFormData(p => ({ ...p, courtOrderNumber: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order Date</label>
                      <input type="date" value={formData.courtOrderDate || ''} onChange={e => setFormData(p => ({ ...p, courtOrderDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" />
                    </div>
                  </div>
                </div>
              )}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Authority Scope</h4>
                <div className="grid grid-cols-2 gap-3">
                  {['serviceBookingAccess', 'planDetailsAccess', 'budgetAccess', 'claimsAccess', 'clinicalNotesAccess'].map(key => (
                    <label key={key} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox"
                        checked={formData.authorityScope?.[key] ?? false}
                        onChange={() => setFormData(p => ({
                          ...p,
                          authorityScope: { ...p.authorityScope, [key]: !p.authorityScope?.[key] }
                        }))}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                      <span className="text-sm capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center gap-2"><X className="w-4 h-4" />Cancel</button>
              <button onClick={handleSave} className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 font-medium"><Save className="w-4 h-4" />{editingId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-red-800 mb-2">Delete Nominee</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to remove <strong>{nominees.find(n => n.id === deleteConfirm)?.firstName} {nominees.find(n => n.id === deleteConfirm)?.lastName}</strong>?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"><Trash2 className="w-4 h-4" />Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NomineeManagement;
